/**
 * Bounding box input dialog (COORD-01).
 *
 * Renders a <dialog> element (defined in index.html.erb as #bbox-dialog) that lets
 * the user enter top-left and bottom-right GPS coordinates in decimal degrees.
 *
 * Features:
 *   - Live area preview (km²) as the user types
 *   - Inline validation with red error messages
 *   - Confirm button disabled until bbox is valid
 *   - Pre-fills fields if a previous bbox is available
 */

import { validateBbox, bboxAreaKm2 } from "../canvas/projection.js";

/**
 * Open the bounding box modal.
 *
 * @param {Function} onConfirm - Called with the confirmed bbox object:
 *   { topLat, leftLng, bottomLat, rightLng }
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number } | null} existingBbox
 *   Pre-fill values if the user is re-editing.
 */
export function openBboxModal(onConfirm, existingBbox = null) {
  const dialog = document.getElementById("bbox-dialog");
  if (!dialog) {
    console.error("openBboxModal: #bbox-dialog element not found");
    return;
  }

  const inputTopLat     = dialog.querySelector("#bbox-top-lat");
  const inputLeftLng    = dialog.querySelector("#bbox-left-lng");
  const inputBottomLat  = dialog.querySelector("#bbox-bottom-lat");
  const inputRightLng   = dialog.querySelector("#bbox-right-lng");
  const areaEl          = dialog.querySelector("#bbox-area");
  const errorsEl        = dialog.querySelector("#bbox-errors");
  const confirmBtn      = dialog.querySelector("#bbox-confirm");
  const cancelBtn       = dialog.querySelector("#bbox-cancel");

  // Pre-fill if editing an existing bbox
  if (existingBbox) {
    inputTopLat.value    = existingBbox.topLat;
    inputLeftLng.value   = existingBbox.leftLng;
    inputBottomLat.value = existingBbox.bottomLat;
    inputRightLng.value  = existingBbox.rightLng;
  } else {
    inputTopLat.value    = "";
    inputLeftLng.value   = "";
    inputBottomLat.value = "";
    inputRightLng.value  = "";
  }

  /** Parse all four fields and return a bbox or null if any is empty/non-numeric. */
  function parseBbox() {
    const topLat    = parseFloat(inputTopLat.value);
    const leftLng   = parseFloat(inputLeftLng.value);
    const bottomLat = parseFloat(inputBottomLat.value);
    const rightLng  = parseFloat(inputRightLng.value);

    if ([topLat, leftLng, bottomLat, rightLng].some(isNaN)) return null;
    return { topLat, leftLng, bottomLat, rightLng };
  }

  /** Update area preview and validation state. */
  function update() {
    const bbox = parseBbox();
    if (!bbox) {
      areaEl.textContent    = "";
      errorsEl.textContent  = "";
      confirmBtn.disabled   = true;
      return;
    }

    const errors = validateBbox(bbox);
    if (errors.length > 0) {
      errorsEl.textContent  = errors.join(" · ");
      areaEl.textContent    = "";
      confirmBtn.disabled   = true;
    } else {
      errorsEl.textContent  = "";
      const area = bboxAreaKm2(bbox);
      areaEl.textContent    = `Approximate area: ${area.toFixed(1)} km²`;
      confirmBtn.disabled   = false;
    }
  }

  // Run update once on open so pre-filled values are reflected
  update();

  // Wire live update on all inputs
  [inputTopLat, inputLeftLng, inputBottomLat, inputRightLng].forEach((input) => {
    input.addEventListener("input", update);
  });

  /** Cleanup: remove listeners added during this open session. */
  function cleanup() {
    [inputTopLat, inputLeftLng, inputBottomLat, inputRightLng].forEach((input) => {
      input.removeEventListener("input", update);
    });
    confirmBtn.removeEventListener("click", handleConfirm);
    cancelBtn.removeEventListener("click", handleCancel);
    dialog.removeEventListener("cancel", handleCancel);
  }

  function handleConfirm() {
    const bbox = parseBbox();
    const errors = validateBbox(bbox);
    if (errors.length > 0) return; // shouldn't happen (button is disabled)

    cleanup();
    dialog.close();
    onConfirm(bbox);
  }

  function handleCancel() {
    cleanup();
    dialog.close();
  }

  confirmBtn.addEventListener("click", handleConfirm);
  cancelBtn.addEventListener("click", handleCancel);
  // Allow Escape key to cancel
  dialog.addEventListener("cancel", handleCancel);

  dialog.showModal();
}
